import React from 'react';
import { Card, Collapse, Descriptions, Progress, Space, Tag, Typography } from 'antd';
import type { EnrichedExecutionStop, ExecutionProgressHydration } from './types';
import { cargoAtStop, isLoadingStop } from './executionHelpers';
import PartialExecutionCargoRow from './PartialExecutionCargoRow';
import SharedUnloadVisualization from './SharedUnloadVisualization';
import OperationalExecutionActions from './OperationalExecutionActions';
import OperationalContextBlock from './OperationalContextBlock';
import {
  availableOperationalActionsForStop,
  type RouteOperationalStatus,
  type HumanizedCargoMeta,
  enrichExecutionCargo,
  isAdvancedExecutionMode,
  formatOperationalDateTime,
  type StopOperationalContext
} from '../routes/execution';
import {
  getExecutionStatusBadgeProps,
  getExecutionStatusLabel,
  normalizeOperationalState
} from '../routes/execution/statusFacade';
import type { ExecutionActionSubmitPayload } from './ExecutionActionModal';

const { Text } = Typography;

type Props = {
  routeId: number;
  hydration: ExecutionProgressHydration;
  stops: EnrichedExecutionStop[];
  routeStatus: RouteOperationalStatus;
  cargoIndex: Map<number, HumanizedCargoMeta>;
  stopContextMap: Map<string, StopOperationalContext>;
  onSubmit: (payload: ExecutionActionSubmitPayload) => Promise<void>;
  onOpenCompletion?: () => void;
  onModeChange?: () => void;
  actionLoading?: boolean;
  readOnly?: boolean;
  forceAdvanced?: boolean;
};

const WORKFLOW_TASK_LABELS: Record<string, string> = {
  LOADING_PHOTO: 'Фото погрузки',
  LOADING_DOCS: 'Документы погрузки',
  LOADING_DOCUMENTS: 'Документы погрузки',
  UNLOADING_PHOTO: 'Фото выгрузки',
  UNLOADING_DOCS: 'Документы выгрузки',
  UNLOADING_DOCUMENTS: 'Документы выгрузки'
};

export const ExecutionStopsPanel: React.FC<Props> = ({
  routeId,
  hydration,
  stops,
  routeStatus,
  cargoIndex,
  stopContextMap,
  onSubmit,
  onOpenCompletion,
  onModeChange,
  actionLoading = false,
  readOnly = false,
  forceAdvanced = isAdvancedExecutionMode()
}) => {
  const items = stops.map((stop, index) => {
    const relatedCargo = enrichExecutionCargo(
      cargoAtStop(hydration.cargo, stop.executionStopUid, stop.type),
      cargoIndex
    );
    const isLoading = isLoadingStop(stop.type);
    const operationalState = normalizeOperationalState(stop.executionState);
    const badge = getExecutionStatusBadgeProps(operationalState);
    const stopActions = availableOperationalActionsForStop(
      stop,
      routeStatus,
      hydration,
      stops,
      forceAdvanced
    );
    const stopContext = stopContextMap.get(stop.executionStopUid);

    return {
      key: stop.executionStopUid,
      label: (
        <Space wrap>
          <Text strong>
            {isLoading ? 'Погрузка' : 'Выгрузка'} {index + 1}
          </Text>
          <Tag color={badge.color}>{badge.label}</Tag>
          {stop.isCompleted ? <Tag color="success">Завершена</Tag> : null}
        </Space>
      ),
      children: (
        <div>
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Тип">{isLoading ? 'Погрузка' : 'Выгрузка'}</Descriptions.Item>
            <Descriptions.Item label="Статус">{getExecutionStatusLabel(operationalState)}</Descriptions.Item>
            <Descriptions.Item label="Адрес">{stop.address ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Контакт">{stop.contactPerson ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Телефон">{stop.phone ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="План">
              {formatOperationalDateTime(stop.plannedAt ?? stopContext?.plannedAt ?? null)}
            </Descriptions.Item>
            <Descriptions.Item label="Факт">
              {formatOperationalDateTime(
                stopContext?.actualAt ??
                  (isLoading
                    ? stop.loadingCompletedAt ?? stop.loadingStartedAt
                    : stop.unloadingCompletedAt ?? stop.unloadingStartedAt)
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Прогресс">
              <Progress percent={stop.completionPercent} size="small" />
            </Descriptions.Item>
          </Descriptions>

          {stopContext ? (
            <div style={{ marginBottom: 12 }}>
              <OperationalContextBlock
                context={stopContext}
                operationType={isLoading ? 'loading' : 'unloading'}
                compact
              />
            </div>
          ) : null}

          {relatedCargo.length > 0 ? (
            <Card size="small" title="Грузы на точке" style={{ marginBottom: 12 }}>
              {relatedCargo.map((c) => (
                <PartialExecutionCargoRow
                  key={`${c.cargoId}-${c.fromExecutionStopUid}`}
                  cargo={enrichExecutionCargo([c], cargoIndex)[0]}
                  compact
                />
              ))}
            </Card>
          ) : null}

          {!isLoading && relatedCargo.length > 1 ? (
            <SharedUnloadVisualization
              unloadStop={stop}
              allStops={hydration.stops}
              edges={hydration.edges}
              cargo={hydration.cargo}
            />
          ) : null}

          {stop.pendingWorkflowTasks.length > 0 ? (
            <Card size="small" title="Документы" style={{ marginTop: 8 }}>
              {stop.pendingWorkflowTasks.map((t) => (
                <div key={t.id}>
                  <Tag>{WORKFLOW_TASK_LABELS[String(t.taskType).toUpperCase()] ?? 'Документ'}</Tag>{' '}
                  <Text type="secondary">открыта</Text>
                </div>
              ))}
            </Card>
          ) : null}

          {!readOnly && stopActions.length > 0 ? (
            <div style={{ marginTop: 12 }}>
              <OperationalExecutionActions
                routeId={routeId}
                actions={stopActions}
                cargoAtStop={relatedCargo}
                onSubmit={onSubmit}
                onOpenCompletion={onOpenCompletion}
                onModeChange={onModeChange}
                loading={actionLoading}
                showModeToggle={false}
              />
            </div>
          ) : null}
        </div>
      )
    };
  });

  return (
    <Card title="Точки маршрута" size="small" style={{ marginTop: 16 }}>
      <Collapse items={items} accordion={items.length > 3} />
    </Card>
  );
};

export default ExecutionStopsPanel;
